import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, CheckCircle, AlertTriangle, Loader, X, Image, File, Edit3, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

function SubirMulta() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [archivo, setArchivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [camposEditados, setCamposEditados] = useState({});
  const [editando, setEditando] = useState(false);
  const [dragActivo, setDragActivo] = useState(false);

  const tiposSoportados = ['image/jpeg', 'image/png', 'image/tiff', 'application/pdf'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActivo(false);
    const file = e.dataTransfer.files[0];
    if (file) validarYSetArchivo(file);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActivo(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActivo(false);
  }, []);

  const validarYSetArchivo = (file) => {
    setError(null);
    setResultado(null);
    setCamposEditados({});

    if (!tiposSoportados.includes(file.type)) {
      setError(`Tipo de archivo no soportado: ${file.type}`);
      return;
    }

    if (file.size > maxSize) {
      setError(`Archivo demasiado grande: ${(file.size / 1024 / 1024).toFixed(1)}MB. Máximo: 10MB`);
      return;
    }

    setArchivo(file);

    // Preview para imágenes
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) validarYSetArchivo(file);
  };

  const analizarDocumento = async () => {
    if (!archivo) return;

    setAnalizando(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('documento', archivo);
      formData.append('empresa_id', usuario?.empresa?.id || '');

      const response = await api.post('/ocr/analizar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResultado(response.data.data);
      setCamposEditados(response.data.data.campos || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Error al analizar el documento');
      setResultado(err.response?.data || null);
    } finally {
      setAnalizando(false);
    }
  };

  const procesarMulta = async () => {
    if (!archivo) return;

    setProcesando(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('documento', archivo);
      formData.append('empresa_id', usuario?.empresa?.id || '');

      // Añadir campos editados
      Object.entries(camposEditados).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      const response = await api.post('/ocr/procesar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Redirigir a la multa creada
      navigate(`/multas?created=${response.data.data.multa_id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al procesar la multa');
    } finally {
      setProcesando(false);
    }
  };

  const limpiar = () => {
    setArchivo(null);
    setPreview(null);
    setResultado(null);
    setError(null);
    setCamposEditados({});
    setEditando(false);
  };

  const getConfianzaColor = (confianza) => {
    if (confianza >= 70) return 'text-green-600 bg-green-100';
    if (confianza >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getConfianzaIcon = (confianza) => {
    if (confianza >= 70) return <CheckCircle className="w-5 h-5" />;
    return <AlertTriangle className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-2">
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Subir Multa (OCR)</h1>
          <p className="text-gray-600">Sube una imagen o PDF de una multa física para extraer los datos automáticamente</p>
        </div>

        {/* Zona de subida */}
        {!archivo && (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
              dragActivo ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}
          >
            <Upload className={`w-16 h-16 mx-auto mb-4 ${dragActivo ? 'text-blue-500' : 'text-gray-400'}`} />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              Arrastra tu documento aquí
            </h3>
            <p className="text-gray-500 mb-4">o haz clic para seleccionar</p>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.tiff,.pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="file-input"
            />
            <label
              htmlFor="file-input"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
            >
              <FileText className="w-5 h-5" />
              Seleccionar archivo
            </label>
            <p className="text-sm text-gray-400 mt-4">
              Formatos: JPG, PNG, TIFF, PDF | Máximo: 10MB
            </p>
          </div>
        )}

        {/* Archivo seleccionado */}
        {archivo && !resultado && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-start gap-4">
              {/* Preview o icono */}
              <div className="flex-shrink-0">
                {preview ? (
                  <img src={preview} alt="Preview" className="w-32 h-32 object-cover rounded-lg border" />
                ) : (
                  <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                    {archivo.type === 'application/pdf' ? (
                      <File className="w-12 h-12 text-red-500" />
                    ) : (
                      <Image className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                )}
              </div>

              {/* Info del archivo */}
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">{archivo.name}</h3>
                <p className="text-sm text-gray-500">
                  {archivo.type} | {(archivo.size / 1024).toFixed(0)} KB
                </p>

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={analizarDocumento}
                    disabled={analizando}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {analizando ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Analizando...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4" />
                        Analizar documento
                      </>
                    )}
                  </button>
                  <button
                    onClick={limpiar}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <X className="w-4 h-4" />
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Error</p>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Resultado del análisis */}
        {resultado && (
          <div className="bg-white rounded-xl shadow-sm p-6 mt-4">
            {/* Confianza */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">Datos Extraídos</h3>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getConfianzaColor(resultado.confianza)}`}>
                {getConfianzaIcon(resultado.confianza)}
                <span className="font-medium">{resultado.confianza}% confianza</span>
              </div>
            </div>

            {resultado.requiere_revision && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <p className="text-yellow-800">Algunos campos requieren revisión manual</p>
              </div>
            )}

            {/* Campos extraídos */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {[
                { key: 'matricula', label: 'Matrícula', required: true },
                { key: 'numero_expediente', label: 'Expediente' },
                { key: 'fecha_infraccion', label: 'Fecha Infracción', type: 'date' },
                { key: 'importe', label: 'Importe (€)', type: 'number' },
                { key: 'organismo_emisor', label: 'Organismo' },
                { key: 'puntos', label: 'Puntos', type: 'number' }
              ].map(({ key, label, required, type }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                  </label>
                  {editando ? (
                    <input
                      type={type || 'text'}
                      value={camposEditados[key] || ''}
                      onChange={(e) => setCamposEditados({ ...camposEditados, [key]: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={`Sin ${label.toLowerCase()}`}
                    />
                  ) : (
                    <div className={`px-3 py-2 bg-gray-50 rounded-lg ${!camposEditados[key] ? 'text-gray-400 italic' : ''}`}>
                      {camposEditados[key] || `Sin ${label.toLowerCase()}`}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Descripción (campo largo) */}
            {resultado.campos?.descripcion && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm">
                  {resultado.campos.descripcion}
                </div>
              </div>
            )}

            {/* Acciones */}
            <div className="flex gap-3 pt-4 border-t">
              {editando ? (
                <button
                  onClick={() => setEditando(false)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Save className="w-4 h-4" />
                  Guardar cambios
                </button>
              ) : (
                <button
                  onClick={() => setEditando(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Edit3 className="w-4 h-4" />
                  Editar campos
                </button>
              )}

              <button
                onClick={procesarMulta}
                disabled={procesando || !camposEditados.matricula}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {procesando ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Creando multa...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Crear Multa
                  </>
                )}
              </button>

              <button
                onClick={limpiar}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Info adicional */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Consejos para mejores resultados:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Asegúrate de que el documento esté bien iluminado y enfocado</li>
            <li>• Evita sombras o reflejos sobre el papel</li>
            <li>• Si es posible, escanea en lugar de fotografiar</li>
            <li>• Los campos extraídos pueden editarse antes de crear la multa</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default SubirMulta;
